import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  currentTemplate: null,
  editingField: null,
  editingSection: null,
  previewMode: false,
  validationErrors: {},
  isDirty: false
};

const templateBuilderSlice = createSlice({
  name: 'templateBuilder',
  initialState,
  reducers: {
    setCurrentTemplate: (state, action) => {
      state.currentTemplate = action.payload;
      state.isDirty = false;
      state.validationErrors = {};
    },
    updateTemplateMetadata: (state, action) => {
      if (state.currentTemplate) {
        state.currentTemplate = { ...state.currentTemplate, ...action.payload };
        state.isDirty = true;
      }
    },
    addSection: (state, action) => {
      if (state.currentTemplate) {
        const newSection = {
          sectionId: action.payload.sectionId,
          sectionName: action.payload.sectionName,
          displayOrder: (state.currentTemplate.sections?.length || 0) + 1,
          required: false,
          collapsible: true,
          collapsed: true
        };
        state.currentTemplate.sections = [...(state.currentTemplate.sections || []), newSection];
        state.isDirty = true;
      }
    },
    updateSection: (state, action) => {
      if (state.currentTemplate?.sections) {
        const index = state.currentTemplate.sections.findIndex(s => s.sectionId === action.payload.sectionId);
        if (index !== -1) {
          state.currentTemplate.sections[index] = { ...state.currentTemplate.sections[index], ...action.payload };
          state.isDirty = true;
        }
      }
    },
    deleteSection: (state, action) => {
      if (state.currentTemplate?.sections) {
        state.currentTemplate.sections = state.currentTemplate.sections.filter(s => s.sectionId !== action.payload);
        // Remove fields in this section
        if (state.currentTemplate.fields) {
          state.currentTemplate.fields = state.currentTemplate.fields.filter(f => f.section !== action.payload);
        }
        state.isDirty = true;
      }
    },
    addField: (state, action) => {
      if (state.currentTemplate) {
        const newField = {
          ...action.payload,
          displayOrder: (state.currentTemplate.fields?.filter(f => f.section === action.payload.section).length || 0) + 1
        };
        state.currentTemplate.fields = [...(state.currentTemplate.fields || []), newField];
        state.isDirty = true;
      }
    },
    updateField: (state, action) => {
      if (state.currentTemplate?.fields) {
        const index = state.currentTemplate.fields.findIndex(f => f.fieldId === action.payload.fieldId);
        if (index !== -1) {
          state.currentTemplate.fields[index] = { ...state.currentTemplate.fields[index], ...action.payload };
          state.isDirty = true;
        }
      }
    },
    deleteField: (state, action) => {
      if (state.currentTemplate?.fields) {
        state.currentTemplate.fields = state.currentTemplate.fields.filter(f => f.fieldId !== action.payload);
        state.isDirty = true;
      }
    },
    setEditingField: (state, action) => {
      state.editingField = action.payload;
    },
    setEditingSection: (state, action) => {
      state.editingSection = action.payload;
    },
    setPreviewMode: (state, action) => {
      state.previewMode = action.payload;
    },
    setValidationErrors: (state, action) => {
      state.validationErrors = action.payload;
    },
    reorderSections: (state, action) => {
      if (state.currentTemplate?.sections) {
        const { startIndex, endIndex } = action.payload;
        const sections = [...state.currentTemplate.sections];
        const [removed] = sections.splice(startIndex, 1);
        sections.splice(endIndex, 0, removed);
        
        // Update display orders
        sections.forEach((section, index) => {
          section.displayOrder = index + 1;
        });
        
        state.currentTemplate.sections = sections;
        state.isDirty = true;
      }
    },
    reorderFields: (state, action) => {
      if (state.currentTemplate?.fields) {
        const { sourceSectionId, destinationSectionId, startIndex, endIndex } = action.payload;
        
        if (sourceSectionId === destinationSectionId) {
          // Reordering within same section
          const sectionFields = state.currentTemplate.fields.filter(f => f.section === sourceSectionId);
          const otherFields = state.currentTemplate.fields.filter(f => f.section !== sourceSectionId);
          
          const [removed] = sectionFields.splice(startIndex, 1);
          sectionFields.splice(endIndex, 0, removed);
          
          // Update display orders for section fields
          sectionFields.forEach((field, index) => {
            field.displayOrder = index + 1;
          });
          
          state.currentTemplate.fields = [...otherFields, ...sectionFields];
        } else {
          // Moving between sections
          const sourceFields = state.currentTemplate.fields.filter(f => f.section === sourceSectionId);
          const destFields = state.currentTemplate.fields.filter(f => f.section === destinationSectionId);
          const otherFields = state.currentTemplate.fields.filter(f => f.section !== sourceSectionId && f.section !== destinationSectionId);
          
          const [movedField] = sourceFields.splice(startIndex, 1);
          movedField.section = destinationSectionId;
          destFields.splice(endIndex, 0, movedField);
          
          // Update display orders
          sourceFields.forEach((field, index) => {
            field.displayOrder = index + 1;
          });
          destFields.forEach((field, index) => {
            field.displayOrder = index + 1;
          });
          
          state.currentTemplate.fields = [...otherFields, ...sourceFields, ...destFields];
        }
        
        state.isDirty = true;
      }
    },
    addListColumn: (state, action) => {
      if (state.currentTemplate) {
        const newColumn = {
          fieldId: action.payload.fieldId,
          label: action.payload.label,
          width: action.payload.width || 20,
          sortable: action.payload.sortable !== false,
          filterable: action.payload.filterable !== false
        };
        state.currentTemplate.listColumns = [...(state.currentTemplate.listColumns || []), newColumn];
        state.isDirty = true;
      }
    },
    updateListColumn: (state, action) => {
      if (state.currentTemplate?.listColumns) {
        const { index, column } = action.payload;
        if (index >= 0 && index < state.currentTemplate.listColumns.length) {
          state.currentTemplate.listColumns[index] = { ...column };
          delete state.currentTemplate.listColumns[index].index; // Remove index property
          state.isDirty = true;
        }
      }
    },
    deleteListColumn: (state, action) => {
      if (state.currentTemplate?.listColumns) {
        const index = action.payload;
        state.currentTemplate.listColumns.splice(index, 1);
        state.isDirty = true;
      }
    },
    reorderListColumns: (state, action) => {
      if (state.currentTemplate?.listColumns) {
        const { startIndex, endIndex } = action.payload;
        const columns = [...state.currentTemplate.listColumns];
        const [removed] = columns.splice(startIndex, 1);
        columns.splice(endIndex, 0, removed);
        state.currentTemplate.listColumns = columns;
        state.isDirty = true;
      }
    },
    clearTemplate: (state) => {
      state.currentTemplate = null;
      state.editingField = null;
      state.editingSection = null;
      state.previewMode = false;
      state.validationErrors = {};
      state.isDirty = false;
    }
  }
});

export const {
  setCurrentTemplate,
  updateTemplateMetadata,
  addSection,
  updateSection,
  deleteSection,
  reorderSections,
  addField,
  updateField,
  deleteField,
  reorderFields,
  addListColumn,
  updateListColumn,
  deleteListColumn,
  reorderListColumns,
  setEditingField,
  setEditingSection,
  setPreviewMode,
  setValidationErrors,
  clearTemplate
} = templateBuilderSlice.actions;

// Selectors
export const selectCurrentTemplate = (state) => state.templateBuilder.currentTemplate;
export const selectEditingField = (state) => state.templateBuilder.editingField;
export const selectEditingSection = (state) => state.templateBuilder.editingSection;
export const selectPreviewMode = (state) => state.templateBuilder.previewMode;
export const selectValidationErrors = (state) => state.templateBuilder.validationErrors;
export const selectIsDirty = (state) => state.templateBuilder.isDirty;

export default templateBuilderSlice.reducer;